<template>
  <AppLoading v-if="loading" />

  <!-- A refused preview is a CORRECT outcome, not an outage, and it must be visible.
       Previously the rejection escaped the handler and the page sat on a spinner. -->
  <div class="IntroducerSchedule__error" v-else-if="error">{{ error }}</div>

  <main class="IntroducerSchedule" v-if="data">
    <!-- PREVIEW BANNER. Rendered only when the payload says persisted === false.
         Under clause 6.10 a display on the platform counts as written notification, so a
         rendering of rows that have not been listed must never be mistakable for the
         notified document. The banner is the visible half of that; the invisible half is
         that the preview endpoint is isAdmin and writes nothing. -->
    <div class="IntroducerSchedule__preview" v-if="isPreview">
      <strong>PREVIEW — nothing is recorded.</strong>
      This is a computed rendering of how Schedule 1 would appear. No entry exists, nothing
      has been notified to the Introducer, and closing this page leaves no trace.
    </div>

    <header class="IntroducerSchedule__head">
      <h1>Schedule 1 — Introduced Clients</h1>
      <h2>Introducer Agreement</h2>

      <div class="row">
        <div class="col-6">
          <h3>Introducer</h3>
          <p>
            <strong>{{ data.introducer.lastname }} {{ data.introducer.firstname }}</strong><br>
            <span v-if="data.introducer.company_name">{{ data.introducer.company_name }}<br></span>
            {{ mapCountry(data.introducer.country) }}
          </p>
        </div>
        <div class="col-6 text-end">
          <h3>Effective Date</h3>
          <!-- NO AGREEMENT, NO EFFECTIVE DATE, AND NOTHING SHAPED LIKE ONE.
               This printed `effectiveDate` unconditionally and a null formatted as
               "1 January 1970" -- an invented contractual term on a surface clause 6.10
               turns into written notification. A blank would not do either: under a
               heading reading "Effective Date", empty space looks like a date that failed
               to load rather than an agreement nobody has configured. Say which it is. -->
          <p v-if="hasEffectiveDate"><strong>{{ effectiveDate }}</strong></p>
          <p v-else class="IntroducerSchedule__absent">
            <strong>Not recorded</strong><br>
            <span v-if="agreementType === 'unconfigured'">
              No Introducer Agreement is configured for this Introducer,
            </span>
            <span v-else>
              The Introducer Agreement recorded for this Introducer carries no Effective Date,
            </span>
            so clause 15.4 has no boundary to apply.
          </p>
        </div>
      </div>
    </header>

    <!-- THE SCHEDULE ITSELF -->
    <section>
      <h3>Listed clients</h3>
      <table class="table IntroducerSchedule__table">
        <thead>
          <tr>
            <th>Account</th>
            <th>Client</th>
            <th>Entity</th>
            <th>Jurisdiction</th>
            <th>Date added to Schedule 1</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in scheduleRows" :key="i" :class="{ 'IntroducerSchedule__removed': row.status === 'removed', 'IntroducerSchedule__would-be': row.would_be }">
            <td>{{ row.account_number || '—' }}</td>
            <td>{{ row.client_name }}</td>
            <td>{{ row.entity_name || '—' }}</td>
            <td>{{ mapCountry(row.jurisdiction) }}</td>
            <!-- A would-be row has NO date, deliberately. `added_at` is written by the
                 listing, and the listing is the notifying act -- printing a date here
                 would date a notification that has not happened. -->
            <td>
              <span v-if="row.would_be" class="IntroducerSchedule__pending">not yet listed</span>
              <span v-else>{{ formatDate(row.added_at, 'd MMMM yyyy') }}</span>
            </td>
            <td>
              <span v-if="row.would_be" class="IntroducerSchedule__pending">
                Would be listed under 15.4 — not notified
              </span>
              <span v-else-if="row.status === 'removed'">
                Removed {{ formatDate(row.removed_at, 'd MMMM yyyy') }}
              </span>
              <span v-else>Listed</span>
            </td>
          </tr>
          <tr v-if="!scheduleRows.length">
            <td colspan="6" class="text-center">No clients listed.</td>
          </tr>
        </tbody>
      </table>

      <!-- A REMOVED ROW STAYS VISIBLE AND CARRIES ITS JUSTIFICATION.
           Listing is notification under 6.10; a row that simply disappeared would leave
           the Introducer unable to see that anything had changed. -->
      <div class="IntroducerSchedule__removal" v-for="(row, i) in removedRows" :key="'r' + i">
        <strong>{{ row.client_name }} — removed {{ formatDate(row.removed_at, 'd MMMM yyyy') }}.</strong>
        Basis: {{ basisLabel(row.removed_basis) }}. Reason: {{ row.removed_reason }}
      </div>
    </section>

    <!-- CLIENTS THAT ARE NOT ADDABLE UNILATERALLY -- AND WHY, WHICH IS NOT ONE ANSWER.
         This section carried a single hardcoded sentence: "Clause 15.4 permits unilateral
         addition only of clients introduced after the Effective Date of X. Each client
         below pre-dates it." With no agreement configured every clause of that was false.
         The clients do not pre-date anything: they are here because `governed` is false,
         and _computeSchedule routes EVERY client to this list when the agreement is not
         `standard`, whatever the classifier returned.

         The distinction is not pedantry. "Requires a signed amendment" and "no agreement
         is configured" have different remedies, and printing the first when the second is
         true sends an Introducer to negotiate a document when what is missing is a row in
         our own database. Under 6.10 this display is the notification, so it has to state
         the real reason. Three states, three sentences. -->
    <section v-if="requiresAmendment.length" class="IntroducerSchedule__amend">
      <!-- ORDER MATTERS: no Effective Date is tested FIRST, ahead of `governed`.
           `governed` is only `agreement_type === 'standard'` and says nothing about the
           date, so a standard agreement with no date would otherwise fall into branch (a)
           and render "after the Effective Date of ." -- a blank standing where a date
           should be, which is the same false-by-omission failure in a quieter costume. A
           model pre('validate') hook makes that state unwritable through mongoose today;
           this ordering means the view does not depend on that staying true. -->
      <h3 v-if="!hasEffectiveDate">Not assessable — no Effective Date is recorded</h3>
      <h3 v-else-if="isGoverned">Requires a signed amendment — outside the clause 15.4 unilateral power</h3>
      <h3 v-else>Not addable — this Introducer is not governed by a standard agreement</h3>
      <!-- (a) NO EFFECTIVE DATE: a configuration that is missing, not a contractual finding. -->
      <p v-if="!hasEffectiveDate">
        <span v-if="agreementType === 'unconfigured'">
          No Introducer Agreement is configured for this Introducer,
        </span>
        <span v-else>
          The Introducer Agreement recorded for this Introducer carries no Effective Date,
        </span>
        so clause 15.4 has no boundary to measure against and <strong>no client below has
        been assessed against it</strong>. This is a configuration that has not been
        entered — it is <strong>not</strong> a finding that these clients require an
        amendment. Record the agreement and its Effective Date, then re-open this preview.
      </p>
      <!-- (b) GOVERNED: the clause genuinely applies and these clients genuinely pre-date it. -->
      <p v-else-if="isGoverned">
        The following linked clients <strong>cannot be added to Schedule 1 by CFT acting
        alone</strong>. Clause 15.4 permits unilateral addition only of clients introduced
        after the Effective Date of {{ effectiveDate }}. Each client below pre-dates it, so
        the only routes onto the Schedule are inclusion in the executed Schedule at
        signature, or a later amendment signed by both parties.
      </p>
      <!-- (c) A DATE EXISTS BUT THE AGREEMENT IS NOT `standard` -- bespoke, or none. The
               platform publishes nothing at all for these, so the per-row 15.4 verdict the
               server still computes does not govern, and is deliberately not shown below. -->
      <p v-else>
        This Introducer's agreement is recorded as <strong>{{ agreementType }}</strong>, not
        <strong>standard</strong>. The platform publishes no Schedule 1 for anyone outside
        the standard Introducer Agreement, so none of the clients below can be listed here
        regardless of when they were introduced.
      </p>
      <table class="table IntroducerSchedule__table">
        <thead>
          <tr>
            <th>Account</th>
            <th>Client</th>
            <th>Entity</th>
            <th>Jurisdiction</th>
            <th>Client record dates from</th>
            <!-- The server computes a per-row reason and has always sent it. This table
                 dropped it and substituted the blanket sentence above. It is the true,
                 per-client explanation, and it is the whole point of the row. -->
            <th v-if="showRowReason">Why this client is not addable</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in requiresAmendment" :key="'a' + i">
            <td>{{ row.account_number || '—' }}</td>
            <td>{{ row.client_name }}</td>
            <td>{{ row.entity_name || '—' }}</td>
            <td>{{ mapCountry(row.jurisdiction) }}</td>
            <td>{{ formatDate(row.created_at, 'd MMMM yyyy', 'not recorded') }}</td>
            <td v-if="showRowReason">{{ row.reason || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-if="proposals.length" class="IntroducerSchedule__proposals">
      <h3>Addable under clause 15.4</h3>
      <p>Introduced after the Effective Date. Listing one is a deliberate act and the display is the notification.</p>
      <ul>
        <li v-for="(row, i) in proposals" :key="'p' + i">{{ row.client_name }} — {{ row.reason }}</li>
      </ul>
    </section>

    <!-- THERE IS DELIBERATELY NO COMMISSION SECTION HERE.
         There was one, and it asserted "No commission is currently due ... the computed
         entitlement is nil. This is a calculated result, not an unfinished record." on a
         payload that carries no commission data at all. Nothing had been computed. Under
         clause 6.10 this page is written notification, so that sentence was a false
         contractual statement on a document that counts -- not a display bug.

         It is removed rather than corrected, and no figure replaces it. The commission
         ledger has ONE writer, the commission engine; making this route a second author of
         entitlement would be exactly the drift surface IntroducerAgreement was split out to
         avoid. An introducer-facing commission surface is its own piece of work, and it is
         a 6.10 display in its own right, so it needs its own design rather than arriving as
         a side effect of a Schedule 1 fix. Until then this page states the Schedule and
         says nothing about money. -->
  </main>
</template>

<script>
  import AppLoading from '@/components/AppLoading.vue'
  import schedulesService from '@/services/schedules.service'

  import { formatDate, hasDate } from '@/utils/date'
  import { mapCountry } from '@/utils/mapping'

  export default {
    components: {
      AppLoading
    },

    props: {
      /* Supplied directly when embedded; otherwise the route id drives a fetch. */
      value: {
        type: Object,
        default: null
      }
    },

    data() {
      return {
        loading: false,
        error: null,
        data: this.value
      }
    },

    watch: {
      /* WATCH THE FULL PATH, NOT `params.id`.
         Both routes render this same component, and /introducers/X/schedule and
         /introducers/X/schedule/preview share an id -- so keying on the id meant moving
         between them never refetched, and the page kept whatever it had loaded first. An
         admin who opened the real Schedule and then clicked preview saw the real Schedule
         with no banner and no proposals, and would have concluded there was nothing to
         list. These two payloads are never interchangeable; the fetch has to follow the
         route that says which one is meant. */
      '$route.fullPath': {
        async handler() {
          const id = this.$route.params.id

          if (!id || this.value) {
            return
          }

          this.loading = true
          this.error = null
          /* Which endpoint is chosen here is a contractual decision, not a UI one.
             `preview` writes nothing and is admin-only; `get` returns the real, listed
             Schedule -- and under clause 6.10 that display IS the notification. They are
             never interchangeable, so the route says which one it means. */
          const isPreviewRoute = !!(this.$route.meta && this.$route.meta.preview)

          try {
            this.data = isPreviewRoute
              ? await schedulesService.preview(id)
              : await schedulesService.get(id)
          } catch (e) {
            /* The preview is admin-only server-side, so an introducer reaching that URL
               is REFUSED -- and that refusal is correct, not an outage. Say so and stop.
               Without this the rejection escaped the handler, `loading` stayed true and
               the page sat on a spinner for ever. Never leave a contractual surface
               displaying nothing while looking like it is still working. */
            this.data = null
            this.error = isPreviewRoute
              ? 'This preview is available to administrators only.'
              : 'Schedule 1 could not be loaded. Nothing has been changed.'
          } finally {
            this.loading = false
          }
        },
        immediate: true
      }
    },

    computed: {
      isPreview() {
        return this.data && this.data.persisted === false
      },

      /* THE QUESTION, ASKED OF THE VALUE -- not of the formatted string.
         The template must know whether an Effective Date exists before it prints one or
         builds a sentence around one. Testing the formatted output for emptiness would put
         that decision back inside the formatter, which is exactly where it went wrong. */
      hasEffectiveDate() {
        return hasDate(this.data.agreement && this.data.agreement.effective_date)
      },

      effectiveDate() {
        return formatDate(this.data.agreement.effective_date, 'd MMMM yyyy')
      },

      /* `governed` is the server's own word for agreement_type === 'standard', and it is
         what actually decides whether anything may be published. The introducer-facing
         payload omits it, but that surface never renders the amendment section. */
      isGoverned() {
        return !!(this.data.agreement && this.data.agreement.governed)
      },

      agreementType() {
        return (this.data.agreement && this.data.agreement.agreement_type) || 'unconfigured'
      },

      /* `row.reason` is the classifier's 15.4 verdict. It is the true explanation when the
         clause is what excluded the client, and MISLEADING when it is not: for a bespoke
         agreement the server still computes a verdict ("Introduced after the Effective
         Date: addable unilaterally under clause 15.4...") which does not govern, and
         printing that beside a "not addable" heading would contradict it. Show the reason
         only on the two branches where it IS the reason. */
      showRowReason() {
        return this.isGoverned || !this.hasEffectiveDate
      },

      /* Real entries always; the server's would-be rows APPENDED, and only on a preview.
         This used to be `entries.length ? entries : would_be_entries` -- a fallback on a
         key the API never sent, so the preview's main table rendered "No clients listed."
         every time, which is precisely the screen the preview exists to show.

         They are concatenated rather than substituted because a preview of an introducer
         who already has listed clients must show BOTH: what is on the Schedule and what
         listing would add. They are never merged into one indistinguishable list -- a
         would-be row carries `would_be`, has no date and no id, and the table labels it
         "not notified". A row that could be mistaken for a listed one would, under 6.10,
         look like a notification that never happened. */
      scheduleRows() {
        const entries = this.data.entries || []

        if (!this.isPreview) {
          return entries
        }

        return entries.concat(this.data.would_be_entries || [])
      },

      removedRows() {
        return (this.data.entries || []).filter((e) => e.status === 'removed')
      },

      requiresAmendment() {
        return this.data.requires_amendment || []
      },

      proposals() {
        return this.data.proposals || []
      }
    },

    methods: {
      formatDate,
      mapCountry,

      basisLabel(b) {
        if (b === 'written_agreement_15_4') {
          return 'written agreement under clause 15.4'
        }

        if (b === 'clause_7_termination') {
          return 'termination under clause 7'
        }

        return b || '—'
      }
    }
  }
</script>

<style lang="sass" scoped>
  @import '@/sass/styles.vars.all.sass'

  .IntroducerSchedule
    background: white
    padding: $spacer-4 $spacer-5
    max-width: 1100px
    margin: 0 auto

    h1
      font-size: 1.6em
      text-transform: uppercase
      color: $primary

    h2
      font-size: 1em
      color: $gray-700
      margin-bottom: $spacer-4

    h3
      font-size: 1em
      text-transform: uppercase
      color: $primary
      margin-top: $spacer-4

  .IntroducerSchedule__preview
    border: 2px dashed $primary
    background: lighten($primary, 55)
    padding: $spacer
    margin-bottom: $spacer-4
    font-size: .95em

  .IntroducerSchedule__table
    font-size: .95em
    vertical-align: middle

    thead th
      background: $primary
      color: white

  .IntroducerSchedule__removed
    text-decoration: line-through
    color: $gray-600

  .IntroducerSchedule__removal
    border-left: 3px solid $gray-600
    padding: $spacer-1 $spacer
    margin-bottom: $spacer-1
    font-size: .9em
    background: $gray-100

  .IntroducerSchedule__amend
    border: 1px solid $gray-400
    background: $gray-100
    padding: $spacer $spacer-2
    margin-top: $spacer-4

  /* An absent Effective Date must not sit in the visual slot a real one occupies. It is
     set in the muted, smaller, non-bold treatment used for "not recorded" elsewhere, so
     that at a glance the header reads as a gap rather than as a term of the agreement. */
  .IntroducerSchedule__absent
    color: $gray-600
    font-size: .9em
    font-weight: normal

  /* A would-be row must never read as a listed one. Under 6.10 a listed row is a
     notification; this one is a proposal on an admin screen and is styled to say so. */
  .IntroducerSchedule__would-be
    background: repeating-linear-gradient(45deg, $gray-100, $gray-100 6px, white 6px, white 12px)
    color: $gray-700
    font-style: italic

  .IntroducerSchedule__pending
    text-transform: uppercase
    font-size: .8em
    letter-spacing: .04em
    color: $gray-700

  .IntroducerSchedule__error
    max-width: 1100px
    margin: $spacer-4 auto
    background: $gray-100
    border-left: 3px solid $primary
    padding: $spacer $spacer-2
</style>
